import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Driver, getListDrivers } from '@/services/api/drivers';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage } from '@/utils/errorHandler';

/**
 * Server-side searchable mitra (driver) options for `SearchableDropdown`.
 *
 * The mitra list is unbounded, so it is never fetched in one `size=9999` call.
 * Instead this hook keeps one page in memory, asks the backend to filter via
 * `search` (matches name, code, phone and NIK) and appends the next page when
 * the dropdown is scrolled to the bottom.
 */

export interface MitraOption {
  value: string;
  label: string;
  data: Driver;
}

export interface UseMitraOptionsParams {
  /** Fetch only while the owning modal/dropdown is actually open. */
  enabled: boolean;
  /** Extra server-side filter, e.g. `NO_HANDOVER` for booking candidates. */
  handoverStates?: string;
}

export interface UseMitraOptionsResult {
  options: MitraOption[];
  loading: boolean;
  hasMore: boolean;
  setSearchTerm: (term: string) => void;
  loadMore: () => void;
  /** Clear the search and reload the first page. */
  reset: () => void;
  /** Resolve a picked option back to its full driver record. */
  getDriver: (id: string | number) => Driver | undefined;
}

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

export const getMitraID = (driver: Driver): string =>
  String(driver.ID ?? driver.id ?? '');

export const getMitraName = (driver: Driver): string =>
  `${driver.first_name || ''} ${driver.last_name || ''}`.trim() ||
  driver.name ||
  '-';

/** One spelling of `CODE - Name`, shared with the read-only/fallback labels. */
export const formatMitraLabel = (code?: string, name?: string): string =>
  `${code || '-'} - ${name || '-'}`;

export const buildMitraLabel = (driver: Driver): string =>
  formatMitraLabel(driver.code, getMitraName(driver));

const dedupeByValue = (options: MitraOption[]): MitraOption[] => {
  const deduped = new Map<string, MitraOption>();
  options.forEach((option) => deduped.set(option.value, option));
  return Array.from(deduped.values());
};

export const useMitraOptions = ({
  enabled,
  handoverStates,
}: UseMitraOptionsParams): UseMitraOptionsResult => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<MitraOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // Bumped to reload the first page even when the search term is unchanged.
  const [reloadToken, setReloadToken] = useState(0);
  // Keeps a slow earlier response from overwriting a newer search.
  const requestIDRef = useRef(0);
  // False while the list still shows an unsearched first page, which is what a
  // reset would rebuild anyway — so resetting then would only refetch it.
  const dirtyRef = useRef(false);

  const reset = useCallback(() => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    requestIDRef.current += 1;
    setSearchTerm('');
    setDebouncedSearch('');
    setOptions([]);
    setPage(1);
    setHasMore(false);
    setLoading(false);
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (enabled) return;
    reset();
  }, [enabled, reset]);

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchPage = useCallback(
    async (targetPage: number, search: string, append: boolean) => {
      const requestID = ++requestIDRef.current;
      setLoading(true);

      try {
        const response = await getListDrivers({
          page: targetPage.toString(),
          size: PAGE_SIZE.toString(),
          active: 'true',
          search: search || undefined,
          sort: 'created_at',
          order: 'desc',
          handoverStates,
        });

        if (requestID !== requestIDRef.current) return;

        const mapped: MitraOption[] = (response.data || []).map((driver) => ({
          value: getMitraID(driver),
          label: buildMitraLabel(driver),
          data: driver,
        }));

        setOptions((prev) =>
          dedupeByValue(append ? [...prev, ...mapped] : mapped)
        );
        setPage(targetPage);
        setHasMore(targetPage < (response.pagination?.lastPage || targetPage));
      } catch (error) {
        if (requestID !== requestIDRef.current) return;
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load mitras')
        );
        if (!append) setOptions([]);
        setHasMore(false);
      } finally {
        if (requestID === requestIDRef.current) setLoading(false);
      }
    },
    [handoverStates]
  );

  useEffect(() => {
    if (!enabled) return;
    fetchPage(1, debouncedSearch, false);
  }, [enabled, debouncedSearch, fetchPage, reloadToken]);

  const updateSearchTerm = useCallback((term: string) => {
    if (term.trim()) dirtyRef.current = true;
    setSearchTerm(term);
  }, []);

  const loadMore = useCallback(() => {
    if (!enabled || loading || !hasMore) return;
    dirtyRef.current = true;
    fetchPage(page + 1, debouncedSearch, true);
  }, [enabled, loading, hasMore, page, debouncedSearch, fetchPage]);

  const driversByID = useMemo(() => {
    const map = new Map<string, Driver>();
    options.forEach((option) => map.set(option.value, option.data));
    return map;
  }, [options]);

  const getDriver = useCallback(
    (id: string | number) => driversByID.get(String(id)),
    [driversByID]
  );

  return {
    options,
    loading,
    hasMore,
    setSearchTerm: updateSearchTerm,
    loadMore,
    reset,
    getDriver,
  };
};

export default useMitraOptions;
